import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { useSelector } from "react-redux";

import instance from "../../../api/instance";
import endpoints from "../../../api/endpoints";
import { formatUnix } from "src/utils/date";
import parseNumber from "../../../utils/parse-number";
import { RootState } from "../../../redux/store";
import { useSyncGlobalPageLoader } from "../../../contexts/global-page-loader-context";

const PAGE_SIZE_OPTIONS = [25, 50, 75, 100, 125, 150];

const GAME_PROVIDERS = [
  { value: "", label: "Select" },
  { value: "ezugi", label: "Ezugi" },
  { value: "ss", label: "Super Spade" },
  { value: "qt", label: "Slot 3 | Holi" },
  { value: "evo", label: "Evolution" },
  { value: "cockfight", label: "CockFight" },
  { value: "ludo", label: "Ludo Classic" },
  { value: "pop-the-ball", label: "PopTheBall" },
  { value: "binary", label: "Binary" },
  { value: "tgs", label: "Slot 2" },
  { value: "slot", label: "Slot" },
  { value: "tgslive", label: "LuckyStreak" },
  { value: "rummy", label: "Rummy" },
  { value: "ludo-lands", label: "Ludo Lands" },
  { value: "vivo", label: "vivo gaming" },
  { value: "snakes-and-ladders", label: "snakes and ladders" },
  { value: "bc", label: "Creedroomz" },
  { value: "smart", label: "Smart Soft" },
  { value: "astar", label: "Astar Game" },
  { value: "ds", label: "Dragoon soft" },
  { value: "tembo", label: "Tembo" },
  { value: "av", label: "Aviator" },
  { value: "bcslot", label: "Pascal Game | Popok" },
  { value: "lottery", label: "Lottery" },
  { value: "scratch", label: "Scratch" },
  { value: "darwin", label: "Darwin" },
  { value: "pg", label: "Pocket Game" },
  { value: "bet", label: "Bet Core" },
  { value: "jilli", label: "Jili" },
  { value: "win", label: "Win" },
  { value: "gemini1", label: "Gemini" },
  { value: "amigo", label: "Amigo" },
  { value: "egt", label: "EGT" },
  { value: "studio21", label: "Studio 21" },
  { value: "beon", label: "Beon Game" },
];

interface IClientOption {
  user_id: string;
  user_name: string;
  display_name: string;
}

interface CasinoRow {
  game_name: string;
  type: string;
  amount: number;
  total: number;
  date: string | null;
  round_id: string;
  transaction_id: string;
}

interface SubmittedCasinoFilters {
  member_id?: string;
  bet_status: string;
  from: number;
  to: number;
  limit: number;
  provider_name?: string;
}

const CasinoReportPage = () => {
  const loggedInUserId = useSelector((state: RootState) => state.user.userData?.user_id ?? "");

  const [reportType, setReportType] = useState("");
  const [gameProvider, setGameProvider] = useState("");
  const [date, setDate] = useState(moment().format("YYYY-MM-DD"));

  // client search
  const [clientSearchText, setClientSearchText] = useState("");
  const [clientOptions, setClientOptions] = useState<IClientOption[]>([]);
  const [selectedClient, setSelectedClient] = useState<IClientOption | null>(null);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const clientWrapperRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clientSearchRequestIdRef = useRef(0);
  const CLIENT_SEARCH_MIN_LENGTH = 2;

  const fetchClients = useCallback(
    async (query: string) => {
      if (!loggedInUserId) return [] as IClientOption[];
      const requestId = ++clientSearchRequestIdRef.current;
      try {
        const { data } = await instance.get("admin/downline-members", {
          params: {
            adminId: loggedInUserId,
            scope: "recursive",
            memberType: "user",
            search: query,
          },
        });
        const rawList: any[] = Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data?.data?.rows)
            ? data.data.rows
            : Array.isArray(data?.data?.members)
              ? data.data.members
              : [];
        const list: IClientOption[] = rawList
          .map((item) => ({
            user_id: item?.user_id ?? item?.admin_id ?? item?.id ?? "",
            user_name: item?.user_name ?? item?.username ?? "",
            display_name:
              item?.display_name ?? item?.displayName ?? item?.user_name ?? item?.username ?? "",
          }))
          .filter((item) => item.user_id);
        if (requestId !== clientSearchRequestIdRef.current) return [] as IClientOption[];
        return list;
      } catch {
        if (requestId !== clientSearchRequestIdRef.current) return [] as IClientOption[];
        return [] as IClientOption[];
      }
    },
    [loggedInUserId]
  );

  const handleClientSearchChange = (value: string) => {
    setClientSearchText(value);
    if (selectedClient) setSelectedClient(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const q = value.toLowerCase().trim();
      if (q.length < CLIENT_SEARCH_MIN_LENGTH) {
        setClientOptions([]);
        setShowClientDropdown(true);
        return;
      }
      const list = await fetchClients(q);
      setClientOptions(list);
      setShowClientDropdown(true);
    }, 200);
  };

  const handleSelectClient = (client: IClientOption) => {
    setSelectedClient(client);
    setClientSearchText(client.display_name || client.user_name);
    setShowClientDropdown(false);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (clientWrapperRef.current && !clientWrapperRef.current.contains(e.target as Node)) {
        setShowClientDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // table data
  const [rows, setRows] = useState<CasinoRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [submittedFilters, setSubmittedFilters] = useState<SubmittedCasinoFilters | null>(null);

  useSyncGlobalPageLoader(loading);
  const [apiTotalRecords, setApiTotalRecords] = useState(0);

  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState("");

  const fetchCasinoReport = useCallback(async (filters: SubmittedCasinoFilters, pageNumber: number) => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = {
        bet_status: filters.bet_status,
        from: filters.from,
        to: filters.to,
        limit: filters.limit,
        page_number: pageNumber,
      };
      if (filters.member_id) params.member_id = filters.member_id;
      if (filters.provider_name) params.provider_name = filters.provider_name;

      const { data } = await instance.get(endpoints.BET_LIST_BY_ROUND, { params });
      const rawList = data?.data?.betList ?? data?.data ?? [];
      const list: CasinoRow[] = Array.isArray(rawList)
        ? rawList.map((item: any) => {
            const transaction = Array.isArray(item?.transactions) ? item.transactions[0] : undefined;
            const rowDate = item?.date ?? transaction?.date ?? "";
            const totalAmount = Number(item?.total_amount ?? 0);
            return {
              game_name: String(item?.game_name ?? transaction?.game_name ?? "-"),
              type: "Debit",
              amount: totalAmount,
              total: -Math.abs(totalAmount),
              date: rowDate ? String(rowDate) : null,
              round_id: String(item?.round_id ?? transaction?.round_id ?? "-"),
              transaction_id: String(transaction?.transaction_id ?? item?.transaction_id ?? "-"),
            };
          })
        : [];
      setRows(list);
      setApiTotalRecords(
        Number(data?.data?.count ?? data?.data?.total_count ?? data?.data?.total ?? data?.count ?? list.length)
      );
    } catch {
      setRows([]);
      setApiTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const nextFilters: SubmittedCasinoFilters = {
      member_id: selectedClient?.user_id ?? (loggedInUserId || undefined),
      bet_status: reportType || "settle",
      from: moment(date).startOf("day").unix(),
      to: moment(date).endOf("day").unix(),
      limit: pageSize,
      provider_name: gameProvider || undefined,
    };
    setSubmittedFilters(nextFilters);
    setCurrentPage(1);
  };

  useEffect(() => {
    if (!submittedFilters) return;
    fetchCasinoReport(submittedFilters, currentPage);
  }, [submittedFilters, currentPage, fetchCasinoReport]);

  const filteredList = useMemo(() => {
    if (!searchText.trim()) return rows;
    const q = searchText.toLowerCase();
    return rows.filter((row) =>
      [row.game_name, row.type, row.round_id, row.transaction_id].some((field) => field?.toLowerCase().includes(q))
    );
  }, [rows, searchText]);

  const effectivePageSize = submittedFilters?.limit ?? pageSize;
  const totalRecords = searchText.trim() ? filteredList.length : apiTotalRecords;
  const totalPages = Math.max(1, Math.ceil(totalRecords / effectivePageSize));
  const pagedList = searchText.trim() ? filteredList : rows;

  const isFirstPage = currentPage <= 1;
  const isLastPage = currentPage >= totalPages;

  return (
    <div className="live-bets-report listing-grid report-wrapper">
      {/* Title */}
      <div className="row">
        <div className="col-12">
          <div className="page-title-box d-flex align-items-center justify-content-between">
            <h4 className="mb-0 font-size-18">Casino Report</h4>
            <div className="page-title-right" />
          </div>
        </div>
      </div>

      {/* Card */}
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              {/* Filter form */}
              <form method="post" onSubmit={handleSubmit}>
                <div className="row row5 mb-3">
                  {/* Casino Type */}
                  <div className="col-xl-2 mb-3">
                    <select
                      name="reportType"
                      className="form-control"
                      value={reportType}
                      onChange={(e) => setReportType(e.target.value)}
                    >
                      <option value="">Select Casino Type</option>
                      <option value="settle">Settled Bets</option>
                      <option value="unsettle">UnSettled Bets</option>
                    </select>
                  </div>

                  {/* User search */}
                  <div className="form-group col-xl-2" ref={clientWrapperRef} style={{ position: "relative" }}>
                    <input
                      type="text"
                      autoComplete="off"
                      spellCheck="false"
                      placeholder="Select option"
                      className="form-control"
                      value={clientSearchText}
                      onChange={(e) => handleClientSearchChange(e.target.value)}
                      onFocus={() => {
                        setClientOptions([]);
                        clientSearchRequestIdRef.current += 1;
                        setShowClientDropdown(true);
                      }}
                    />
                    {showClientDropdown && (
                      <ul
                        className="list-group"
                        style={{
                          position: "absolute",
                          zIndex: 1000,
                          width: "100%",
                          maxHeight: "300px",
                          overflowY: "auto",
                          boxShadow: "0 2px 8px rgba(0,0,0,.15)",
                        }}
                      >
                        {clientOptions.length === 0 ? (
                          <li className="list-group-item">
                            {clientSearchText.trim().length >= CLIENT_SEARCH_MIN_LENGTH
                              ? "No elements found. Consider changing the search query."
                              : "List is empty."}
                          </li>
                        ) : (
                          clientOptions.map((client) => (
                            <li
                              key={client.user_id}
                              className="list-group-item list-group-item-action"
                              style={{ cursor: "pointer", padding: "6px 12px", fontSize: "13px" }}
                            >
                              <button
                                type="button"
                                className="btn btn-link p-0 text-left w-100"
                                style={{ textDecoration: "none", color: "inherit", fontSize: "inherit" }}
                                onClick={() => handleSelectClient(client)}
                              >
                                {client.display_name || client.user_name}
                              </button>
                            </li>
                          ))
                        )}
                      </ul>
                    )}
                  </div>

                  {/* Date */}
                  {reportType !== "unsettle" && (
                    <div className="col-xl-2 mb-3">
                      <input
                        type="date"
                        className="form-control"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                      />
                    </div>
                  )}

                  {/* Game Provider */}
                  <div className="col-xl-2 mb-3">
                    <select
                      name="gtype"
                      className="form-control"
                      value={gameProvider}
                      onChange={(e) => setGameProvider(e.target.value)}
                    >
                      {GAME_PROVIDERS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Submit */}
                  <div className="col-xl mb-3">
                    <button type="submit" className="btn btn-primary" disabled={loading}>
                      {loading ? "Loading..." : "Submit"}
                    </button>
                  </div>
                </div>
              </form>

              {/* Show entries + Search */}
              <div className="row">
                <div className="col-6">
                  <div id="tickets-table_length" className="dataTables_length">
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label className="d-inline-flex align-items-center">
                      Show&nbsp;
                      <select
                        className="custom-select custom-select-sm"
                        value={pageSize}
                        onChange={(e) => {
                          setPageSize(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                      >
                        {PAGE_SIZE_OPTIONS.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                      &nbsp;entries
                    </label>
                  </div>
                </div>
                <div className="col-6 text-right">
                  <div id="tickets-table_filter" className="dataTables_filter text-md-right">
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label className="d-inline-flex align-items-center">
                      <input
                        type="search"
                        placeholder="Search..."
                        className="form-control form-control-sm ml-2 form-control"
                        value={searchText}
                        onChange={(e) => {
                          setSearchText(e.target.value);
                          setCurrentPage(1);
                        }}
                      />
                    </label>
                  </div>
                </div>
              </div>

              {/* Table */}
              <div className="table-responsive mb-0 mt-2 report-table">
                <div className="table table-bordered no-footer table-hover table-responsive-sm">
                  <table
                    id="eventsListTbl"
                    role="table"
                    aria-busy="false"
                    aria-colcount={7}
                    className="table b-table table-striped table-bordered"
                  >
                    <thead>
                      <tr role="row">
                        <th role="columnheader" scope="col" aria-colindex={1} className="position-relative">
                          <div>Game Name</div>
                        </th>
                        <th role="columnheader" scope="col" aria-colindex={2} className="position-relative">
                          <div>Type</div>
                        </th>
                        <th role="columnheader" scope="col" aria-colindex={3} className="position-relative text-right">
                          <div>Amount</div>
                        </th>
                        <th role="columnheader" scope="col" aria-colindex={4} className="position-relative text-right">
                          <div>Total</div>
                        </th>
                        <th role="columnheader" scope="col" aria-colindex={5} className="position-relative">
                          <div>Date</div>
                        </th>
                        <th role="columnheader" scope="col" aria-colindex={6} className="position-relative">
                          <div>Round Id</div>
                        </th>
                        <th role="columnheader" scope="col" aria-colindex={7} className="position-relative">
                          <div>Transaction Id</div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagedList.length === 0 ? (
                        <tr className="b-table-empty-row">
                          <td colSpan={7}>
                            <div role="alert" aria-live="polite">
                              <p className="text-center mb-0">
                                {loading ? "Loading..." : "No data available in table"}
                              </p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        pagedList.map((row, idx) => (
                          <tr key={idx} role="row">
                            <td aria-colindex={1}>{row.game_name}</td>
                            <td aria-colindex={2}>{row.type}</td>
                            <td aria-colindex={3}>
                              <p className="text-right mb-0">
                                {parseNumber({ value: row.amount, type: "float" }).toFixed(2)}
                              </p>
                            </td>
                            <td aria-colindex={4}>
                              <p className="text-right mb-0">
                                {parseNumber({ value: row.total, type: "float" }).toFixed(2)}
                              </p>
                            </td>
                            <td aria-colindex={5}>
                              {row.date
                                ? moment(row.date).isValid()
                                  ? moment(row.date).format("DD/MM/YYYY HH:mm:ss")
                                  : formatUnix({ unix: row.date, format: "DD/MM/YYYY HH:mm:ss" })
                                : "-"}
                            </td>
                            <td aria-colindex={6}>{row.round_id}</td>
                            <td aria-colindex={7}>{row.transaction_id}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination */}
              <div className="row pt-3">
                <div className="col">
                  <div className="dataTables_paginate paging_simple_numbers float-right">
                    <ul className="pagination pagination-rounded mb-0">
                      <ul
                        role="menubar"
                        aria-disabled="false"
                        aria-label="Pagination"
                        className="pagination dataTables_paginate paging_simple_numbers my-0 b-pagination justify-content-end"
                      >
                        <li role="presentation" className={`page-item ${isFirstPage ? "disabled" : ""}`}>
                          <button
                            role="menuitem"
                            type="button"
                            aria-label="Go to first page"
                            disabled={isFirstPage}
                            tabIndex={isFirstPage ? -1 : 0}
                            className="page-link"
                            onClick={() => setCurrentPage(1)}
                          >
                            &laquo;
                          </button>
                        </li>
                        <li role="presentation" className={`page-item ${isFirstPage ? "disabled" : ""}`}>
                          <button
                            role="menuitem"
                            type="button"
                            aria-label="Go to previous page"
                            disabled={isFirstPage}
                            tabIndex={isFirstPage ? -1 : 0}
                            className="page-link"
                            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                          >
                            &lsaquo;
                          </button>
                        </li>
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                          <li
                            key={page}
                            role="presentation"
                            className={`page-item ${currentPage === page ? "active" : ""}`}
                          >
                            <button
                              role="menuitemradio"
                              type="button"
                              aria-label={`Go to page ${page}`}
                              aria-checked={currentPage === page}
                              aria-posinset={page}
                              aria-setsize={totalPages}
                              tabIndex={currentPage === page ? 0 : -1}
                              className="page-link"
                              onClick={() => setCurrentPage(page)}
                            >
                              {page}
                            </button>
                          </li>
                        ))}
                        <li role="presentation" className={`page-item ${isLastPage ? "disabled" : ""}`}>
                          <button
                            role="menuitem"
                            type="button"
                            aria-label="Go to next page"
                            disabled={isLastPage}
                            tabIndex={isLastPage ? -1 : 0}
                            className="page-link"
                            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                          >
                            &rsaquo;
                          </button>
                        </li>
                        <li role="presentation" className={`page-item ${isLastPage ? "disabled" : ""}`}>
                          <button
                            role="menuitem"
                            type="button"
                            aria-label="Go to last page"
                            disabled={isLastPage}
                            tabIndex={isLastPage ? -1 : 0}
                            className="page-link"
                            onClick={() => setCurrentPage(totalPages)}
                          >
                            &raquo;
                          </button>
                        </li>
                      </ul>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CasinoReportPage;
